import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, Text, Card, Modal } from '@teals/ui-web';
import {
  useGetBillingBudgetQuery,
  useGetUsageChargesQuery,
  useUpdateBillingBudgetMutation,
} from '../../store/api/billingApi';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AppBudgetRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border, #e2e8f0);
`;

const Payments: React.FC = () => {
  const { data: budget } = useGetBillingBudgetQuery();
  const { data: charges } = useGetUsageChargesQuery();
  const [updateBudget, { isLoading: saving }] = useUpdateBillingBudgetMutation();
  const [tokenCap, setTokenCap] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const cap = budget?.organisation?.budgetConfig?.tokenCap;
    if (cap != null) setTokenCap(String(cap));
  }, [budget]);

  const onSaveOrgBudget = async () => {
    await updateBudget({
      budgetConfig: {
        tokenCap: tokenCap ? Number(tokenCap) : undefined,
        period: 'monthly',
      },
    }).unwrap();
  };

  return (
    <PageContainer>
      <Text variant="h2">Payments</Text>
      <Text variant="body2" style={{ color: 'var(--text-secondary)' }}>
        Charges use your negotiated rate. For token drivers, open{' '}
        <Link to="/analytics">Analytics</Link>.
      </Text>

      <SectionCard padding={24}>
        <Text variant="h4">Organisation budget</Text>
        <FieldGrid>
          <Input
            label="Monthly token cap"
            value={tokenCap}
            onChange={(e) => setTokenCap(e.target.value)}
            placeholder="e.g. 10000000"
          />
        </FieldGrid>
        <Button variant="primary" onClick={onSaveOrgBudget} disabled={saving}>
          {saving ? 'Saving…' : 'Save org budget'}
        </Button>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Application budgets</Text>
        {(budget?.applications || []).map((app) => (
          <AppBudgetRow key={app.applicationId}>
            <span>{app.name}</span>
            <span>
              cap{' '}
              {(app.budgetConfig as { tokenCap?: number } | null)?.tokenCap ??
                '—'}{' '}
              · <Link to={`/applications/${app.applicationId}`}>Edit</Link>
            </span>
          </AppBudgetRow>
        ))}
        {!budget?.applications?.length && (
          <Text variant="body2">No applications yet.</Text>
        )}
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Current-period usage charges</Text>
        {charges?.amountUsd != null ? (
          <>
            <Text>
              {charges.tokensTotal.toLocaleString()} tokens × $
              {charges.ratePerMtokUsd}/MTok ={' '}
              <strong>
                ${charges.amountUsd} {charges.currency}
              </strong>
            </Text>
            <Text variant="body2" style={{ color: 'var(--text-secondary)' }}>
              Rate card {charges.rateCardVersion}
            </Text>
          </>
        ) : (
          <Text variant="body2">
            {charges?.message ||
              'Negotiated rate card not configured yet. Token usage is still tracked on Analytics.'}
          </Text>
        )}
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Payment methods</Text>
        <Text variant="body2">
          Payment provider integration is TBD. Profile and card capture will land
          with the billing provider.
        </Text>
        <Button variant="outline" onClick={() => setModalOpen(true)}>
          Add payment method
        </Button>
      </SectionCard>

      <SectionCard padding={24}>
        <Text variant="h4">Invoices</Text>
        <Text variant="body2">No invoices yet (provider stub).</Text>
      </SectionCard>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Payment Method"
        message="Payment method integration will be available when the billing provider is connected."
        acceptButtonText="OK"
      />
    </PageContainer>
  );
};

export default Payments;
